import { plateFont, codeFont, numFont } from '../../../utils/fonts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import UndoIcon from '@mui/icons-material/Undo';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import axios from 'axios';
import { withTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from 'store';
import { formatDate, formatQuantity, getCsrfToken } from '../../../utils';
import { formatPrice } from '../../../utils/vat';
import { can } from '../../../utils/access';
import { UNIT_OPTIONS } from '../../../utils/units';
import DecimalField from '../../shared/DecimalField';
import {
  FLAG_LABEL_KEYS,
  INFO_FLAGS,
  ROW_TYPE_LABEL_KEYS,
  TERMINAL_WO_STATES,
  translateReason,
} from './inboundStatus';
import { NoteStatusChip, ResolutionChip } from './InboundStatusChip';
import SpreadsheetPreview from './SpreadsheetPreview';

const KIND_ICONS = {
  pdf: <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />,
  image: <ImageOutlinedIcon sx={{ fontSize: 15 }} />,
  text: <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />,
  spreadsheet: <TableChartOutlinedIcon sx={{ fontSize: 15 }} />,
};

// Discount percentages go through the same fi formatter as every quantity here
// ("20 %", "12,5"), with the plain hyphen so a negative balance reads the same as
// the "-1 ×" quantity on the row. Money is always formatPrice.
function formatPercent(value) {
  return formatQuantity(value).replace('\u2212', '-');
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kt`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mt`;
}

function ConfidenceChip({ value, t }) {
  if (value == null) return null;
  const v = parseFloat(value);
  if (v >= 0.9) return null; // high confidence: no noise
  const low = v < 0.75;
  return (
    <Tooltip title={t('inboundDeliveries.confidenceTooltip')}>
      <Chip
        label={`${Math.round(v * 100)} %`}
        size="small"
        sx={{
          height: 20,
          fontSize: '11px',
          fontWeight: 600,
          bgcolor: low ? '#fef3e2' : '#f1f5f9',
          color: low ? '#b45309' : '#64748b',
        }}
      />
    </Tooltip>
  );
}

// --- Line action panel -------------------------------------------------------------

const WO_SEARCH_DEBOUNCE_MS = 300;
// The "Kirjataan" line refetches from the backend on every edit; typing gets a pause.
const PREVIEW_DEBOUNCE_MS = 300;

function workOrderLabel(wo) {
  if (!wo) return '';
  const parts = [`#${wo.work_number}`, wo.registration_number, wo.vehicle_name, wo.customer_name];
  return parts.filter(Boolean).join(' · ');
}

// "−95 %" with the typographic minus the mockup uses for a signed change.
function formatSignedPercent(value) {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return '';
  const abs = formatPercent(Math.abs(n));
  return n < 0 ? `−${abs}` : n > 0 ? `+${abs}` : abs;
}

// Unit-aware "Litroina (40 l)" for the content side of the toggle.
function contentModeLabel(t, qty, unit) {
  const key = `inboundDeliveries.bookAsContent_${unit}`;
  const generic = t('inboundDeliveries.bookAsContent', { qty, unit });
  const specific = t(key, { qty, unit });
  return specific === key ? generic : specific;
}

/**
 * The one "Kirjataan" line every row renders from booking_preview:
 *   Kirjataan   40 l (2 × 20 l) · ≈ 7,30 €/l          291,89 € alv 0 %
 * "≈" only when the unit cost was rebased (content mode); a package row's unit
 * cost is exact. Money keys are null without costs.view_purchase_prices, and
 * then only the quantity prints.
 */
function BookingSummary({ t, preview, credit, loading, warn }) {
  if (!preview) return null;
  const qty = formatQuantity(parseFloat(preview.booked_qty));
  const unit = preview.booked_unit;
  const rebased = preview.mode === 'content';
  const caption =
    rebased && preview.package_size
      ? ` (${formatQuantity(parseFloat(preview.package_count))} × ${formatQuantity(parseFloat(preview.package_size))} ${preview.package_unit || unit})`
      : '';
  const perUnit =
    preview.unit_cost != null ? ` · ${rebased ? '≈ ' : ''}${formatPrice(preview.unit_cost)} €/${unit}` : '';
  return (
    <Box
      data-testid="line-booking-summary"
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1.5,
        flexWrap: 'wrap',
        p: '10px 14px',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: warn ? '#f0d493' : 'divider',
        borderRadius: '10px',
        opacity: loading ? 0.6 : 1,
        transition: 'opacity 120ms',
      }}
    >
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', minWidth: 72 }}>
        {t(credit ? 'inboundDeliveries.returnAsLabel' : 'inboundDeliveries.bookAsLabel')}
      </Typography>
      <Typography sx={{ ...numFont, fontSize: '13px', fontWeight: 600, flex: 1, minWidth: 0 }}>
        {qty} {unit}
        {caption && (
          <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '12px' }}>
            {caption}
          </Box>
        )}
        {perUnit}
      </Typography>
      {preview.booked_total != null && (
        <Typography sx={{ ...numFont, fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {formatPrice(preview.booked_total)} €
          {preview.doc_vat_basis === 'incl' && (
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '11px', ml: 0.5 }}>
              {t('inboundDeliveries.vatExcludedShort')}
            </Box>
          )}
        </Typography>
      )}
    </Box>
  );
}

function LineActions({
  t,
  note,
  line,
  workOrderOptions,
  tracking,
  busy,
  errorDetail,
  onResolve,
  onClose,
  canSeeCosts,
  onPreviewChange,
}) {
  // A credit row (hyvitys / palautus): the goods went back to the supplier. Nothing
  // arrived, so "mark received" is not offered and stock moves DOWN (a vendor
  // return). On an untracked part the return enables tracking at 0 and lands
  // negative — allowed, but only as the user's explicit choice: the inventory
  // option defaults off for it and the panel previews the resulting balance.
  const credit = !!line.is_credit;
  const partInfo = line.suggested_part_info;
  const partTracked = !!partInfo?.inventory_tracked;
  // The order line this row binds to was already received with a stock purchase
  // (manual Receive dialog): booking it again would count the goods twice.
  const inventoryBooked = !credit && !!line.suggested_order_line_info?.inventory_booked;
  const stockDefault = tracking && (!credit || partTracked) && !inventoryBooked;
  // A coded refusal from the backend for THIS panel's last action: the duplicate
  // part case carries the existing work-order line so the user can mark it
  // received instead of hitting a dead end.
  const duplicate = errorDetail?.code === 'duplicate_part_on_work_order' ? errorDetail : null;
  const suggestedMp = credit ? null : line.suggested_maintenance_part_info;
  const initialMode = suggestedMp ? 'received' : line.suggested_maintenance_info ? 'attach' : 'stock';
  const [mode, setMode] = useState(initialMode);
  // Picked work order. Only a per-line suggestion is preselected: when the lahete
  // carries no plate the default list is just the newest open work orders, and
  // silently preselecting the first of those attaches the part to somebody else's job.
  const [pickedWo, setPickedWo] = useState(line.suggested_maintenance_info || null);
  const [woOptions, setWoOptions] = useState(workOrderOptions);
  const [woInput, setWoInput] = useState('');
  const [woLoading, setWoLoading] = useState(false);
  const woDebounceRef = useRef(null);
  const woRequestRef = useRef(0);
  const woId = pickedWo ? pickedWo.id : '';
  const selectedWo = mode === 'received' ? line.suggested_maintenance_info : pickedWo;
  const terminal = selectedWo ? TERMINAL_WO_STATES.includes(selectedWo.state) : false;
  const priceDiffReason = (line.suggestion_reasons || []).find((r) => r.code === 'wo_part_price_diff');

  const [bookStock, setBookStock] = useState(stockDefault && !terminal);
  const [updatePrice, setUpdatePrice] = useState(!credit && !!priceDiffReason);
  const [ack, setAck] = useState(false);
  // Purchase price override, per DOCUMENT unit (per package), alv 0 %. Empty and
  // untouched means "the price derived from the document": the backend computes
  // it and nothing is posted. Only a typed value is ever sent.
  const [priceOpen, setPriceOpen] = useState(false);
  const [unitCost, setUnitCost] = useState('');
  const [costTouched, setCostTouched] = useState(false);
  // Package → content (1 tynnyri booked as 20 l). A size token in the row name
  // offers the toggle; the quantity editor covers everything the regex misses,
  // including typing the content per package by hand. A matched part that is
  // already in a content unit (l) is booked in that unit with no toggle. Content
  // mode is preselected only when the litre twin already exists (the twin IS
  // the memory) or the matched part is in that unit; an unmatched row is kpl.
  const content = line.package_content || null;
  const partUnit = partInfo?.unit || 'kpl';
  const twin = content?.content_part_info || null;
  const lockedToContent = partUnit !== 'kpl';
  const initialBooking = content && (twin || partUnit === content.unit) ? 'content' : 'package';
  const [bookingMode, setBookingMode] = useState(initialBooking);
  const [qtyOpen, setQtyOpen] = useState(false);
  // Content per package typed by hand (no size token in the name).
  const [manualSize, setManualSize] = useState('');
  const [manualUnit, setManualUnit] = useState(content?.unit || (partUnit !== 'kpl' ? partUnit : 'l'));
  const [customQty, setCustomQty] = useState(String(Math.abs(parseFloat(line.quantity)) || 1));
  const [customUnit, setCustomUnit] = useState(partUnit);
  // An existing part picked as the booking target: a hand-made litre part
  // without the barrel code, or any same-unit part for an unmatched row, so no
  // duplicate gets created. Reset whenever the booked unit changes.
  const [pickedPart, setPickedPart] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [partOptions, setPartOptions] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const contentDebounceRef = useRef(null);
  const contentRequestRef = useRef(0);

  // Terminal state depends on the picked WO — keep the stock default in sync.
  useEffect(() => {
    setBookStock(stockDefault && !terminal);
    setAck(false);
  }, [terminal, stockDefault]);

  // Backend refused the stock booking because the order receipt already did it:
  // untick the option so the retry goes through.
  useEffect(() => {
    if (errorDetail?.code === 'inventory_already_booked') setBookStock(false);
  }, [errorDetail]);

  useEffect(() => () => window.clearTimeout(woDebounceRef.current), []);

  // Server-side typeahead over every work order in the org (work number, plate,
  // customer, vehicle). The default options only cover the newest open ones.
  const searchWorkOrders = (term) => {
    window.clearTimeout(woDebounceRef.current);
    const query = term.trim();
    const requestId = ++woRequestRef.current;
    if (!query) {
      setWoOptions(workOrderOptions);
      setWoLoading(false);
      return;
    }
    setWoLoading(true);
    woDebounceRef.current = window.setTimeout(async () => {
      try {
        const response = await axios.get('/api/inbound-delivery-notes/work-order-search/', {
          params: { search: query },
        });
        if (requestId !== woRequestRef.current) return;
        setWoOptions(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        if (requestId === woRequestRef.current) setWoOptions([]);
      } finally {
        if (requestId === woRequestRef.current) setWoLoading(false);
      }
    }, WO_SEARCH_DEBOUNCE_MS);
  };

  const needsAck = terminal && (mode === 'received' || mode === 'attach');

  // What the row is booked as: the printed quantity (package), the package's
  // content (20 l, detected or typed as content per package), or whatever the
  // quantity override says.
  const printedQty = Math.abs(parseFloat(line.quantity)) || 1;
  const manualSizeNum = parseFloat(String(manualSize).replace(',', '.'));
  const sizeKnown = !!content || manualSizeNum > 0;
  const contentUnit = content ? content.unit : manualUnit;
  const contentQty = content
    ? parseFloat(content.booked_quantity)
    : manualSizeNum > 0
    ? Math.round(printedQty * manualSizeNum * 100) / 100
    : null;
  const bookedQtyRaw =
    bookingMode === 'content' ? contentQty : bookingMode === 'custom' ? customQty : null;
  const bookedQty = bookedQtyRaw != null ? parseFloat(String(bookedQtyRaw).replace(',', '.')) : printedQty;
  const bookedUnit = bookingMode === 'content' ? contentUnit : bookingMode === 'custom' ? customUnit : partUnit;
  const nonDefault = mode === 'stock' && bookingMode !== 'package' && bookedQtyRaw != null;
  // Attach and mark-received always take the printed row; only a stock booking
  // can be rebased to the package's content.
  const effectiveBooking = mode === 'stock' ? bookingMode : 'package';
  // The stock lands on a separate content part (found, picked or created).
  const unitChanges = nonDefault && bookedUnit !== partUnit;
  const twinForUnit = twin && twin.unit === bookedUnit ? twin : null;
  // Where the stock lands when it is not simply the matched part: the litre twin,
  // a picked part, or (unmatched row) a part Vahvista will create.
  const showTarget = mode === 'stock' && (unitChanges || !partInfo);
  const targetPart = unitChanges ? twinForUnit || pickedPart : !partInfo ? pickedPart : null;
  const showToggle = mode === 'stock' && !lockedToContent && sizeKnown && contentQty > 0;
  const blocked = (needsAck && !ack) || busy || (mode === 'attach' && !woId) || (nonDefault && !(bookedQty > 0));

  // --- Live preview from the backend --------------------------------------------
  // The default state renders the row's own booking_preview; any edit (mode
  // toggle, quantity, price, picked part) refetches on a debounce. The panel
  // never multiplies money itself.
  const typedCost = costTouched && unitCost !== '' && unitCost != null ? String(unitCost).replace(',', '.') : null;
  const isDefault =
    typedCost == null &&
    (mode === 'stock' ? bookingMode === initialBooking && !pickedPart && !(manualSizeNum > 0) : initialBooking === 'package');
  const [fetchedPreview, setFetchedPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewDebounceRef = useRef(null);
  const previewRequestRef = useRef(0);
  // While an edit's preview is in flight the last known one stays on screen
  // (dimmed), so the box never blinks out between keystrokes.
  const preview = isDefault ? line.booking_preview : fetchedPreview || line.booking_preview;
  const previewKey = JSON.stringify([
    isDefault,
    nonDefault ? String(bookedQtyRaw) : null,
    nonDefault ? bookedUnit : null,
    typedCost,
    pickedPart?.id || null,
    manualSizeNum > 0 ? manualSizeNum : null,
    mode,
  ]);
  useEffect(() => {
    window.clearTimeout(previewDebounceRef.current);
    if (isDefault || mode === 'skip') {
      setPreviewLoading(false);
      return undefined;
    }
    const requestId = ++previewRequestRef.current;
    setPreviewLoading(true);
    previewDebounceRef.current = window.setTimeout(async () => {
      try {
        const params = { line_id: line.id };
        const partId = (showTarget && pickedPart ? pickedPart.id : line.suggested_part) || null;
        if (partId) params.part_id = partId;
        if (nonDefault) {
          params.booked_quantity = String(bookedQtyRaw).replace(',', '.');
          params.booked_unit = bookedUnit;
          if (!content && manualSizeNum > 0 && bookingMode === 'content') {
            params.package_size = Math.round(manualSizeNum * 1000) / 1000;
          }
        }
        if (typedCost != null) params.unit_cost = typedCost;
        const response = await axios.get(`/api/inbound-delivery-notes/${note.id}/line-preview/`, { params });
        if (requestId !== previewRequestRef.current) return;
        setFetchedPreview(response.data?.booking_preview || null);
      } catch (err) {
        if (requestId === previewRequestRef.current) setFetchedPreview(null);
      } finally {
        if (requestId === previewRequestRef.current) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(previewDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, note.id, line.id]);

  // The collapsed row above mirrors the open panel ("40 l · ≈ 7,30 €/l").
  useEffect(() => {
    onPreviewChange?.(line.id, mode === 'skip' ? null : preview);
  }, [preview, mode, line.id, onPreviewChange]);
  useEffect(() => () => onPreviewChange?.(line.id, null), [line.id, onPreviewChange]);

  // A picked part is only valid in the unit it was picked for.
  useEffect(() => {
    setPickedPart(null);
    setPickerOpen(false);
  }, [bookedUnit]);

  // Existing same-unit parts for the target picker: the server's name-based
  // candidates (litre twins) first, then the org-wide part search.
  const defaultPartOptions = () => (content && content.unit === bookedUnit ? content.candidates || [] : []);
  const searchParts = (term) => {
    window.clearTimeout(contentDebounceRef.current);
    const query = term.trim();
    const requestId = ++contentRequestRef.current;
    if (!query) {
      setPartOptions(defaultPartOptions());
      setPartsLoading(false);
      return;
    }
    setPartsLoading(true);
    contentDebounceRef.current = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: query, typeahead: 'true' });
        const response = await axios.get(`/api/parts/?${params.toString()}`);
        if (requestId !== contentRequestRef.current) return;
        const results = response.data.results || response.data;
        setPartOptions(
          (Array.isArray(results) ? results : [])
            .filter((p) => (p.unit || 'kpl') === bookedUnit)
            .map((p) => ({ id: p.id, name: p.name, code: p.primary_code || p.manufacturer_code || '', unit: p.unit || 'kpl' }))
        );
      } catch (err) {
        if (requestId === contentRequestRef.current) setPartOptions([]);
      } finally {
        if (requestId === contentRequestRef.current) setPartsLoading(false);
      }
    }, WO_SEARCH_DEBOUNCE_MS);
  };

  // Balance preview for a credit row returned from stock: "1 → 0", or "0 → -1"
  // when tracking is about to be enabled by the return itself. A content
  // booking previews the content part's balance, not the barrel's.
  const previewPart = unitChanges ? targetPart : partInfo;
  const previewTracked = unitChanges ? !!targetPart?.inventory_tracked : partTracked;
  const stockBefore = previewTracked ? parseFloat(previewPart.quantity_in_stock) : 0;
  const stockAfter = stockBefore - bookedQty;
  const balanceParams = { before: formatQuantity(stockBefore), after: formatQuantity(stockAfter) };

  const modes = [
    suggestedMp
      ? {
          id: 'received',
          icon: <TaskAltIcon sx={{ fontSize: 16 }} />,
          label: t('inboundDeliveries.modeReceived'),
          sub: t('inboundDeliveries.modeReceivedSub'),
        }
      : null,
    {
      id: 'attach',
      icon: <PlaylistAddIcon sx={{ fontSize: 16 }} />,
      label: t(credit ? 'inboundDeliveries.modeAttachCredit' : 'inboundDeliveries.modeAttach'),
      sub: t(credit ? 'inboundDeliveries.modeAttachCreditSub' : 'inboundDeliveries.modeAttachSub'),
    },
    {
      id: 'stock',
      icon: credit ? <UndoIcon sx={{ fontSize: 16 }} /> : <WarehouseIcon sx={{ fontSize: 16 }} />,
      label: t(credit ? 'inboundDeliveries.modeStockCredit' : 'inboundDeliveries.modeStock'),
      sub: t(credit ? 'inboundDeliveries.modeStockCreditSub' : 'inboundDeliveries.modeStockSub'),
    },
    {
      id: 'skip',
      icon: <BlockIcon sx={{ fontSize: 16 }} />,
      label: t('inboundDeliveries.modeSkip'),
      sub: t('inboundDeliveries.modeSkipSub'),
    },
  ].filter(Boolean);

  // Only a typed price is posted; an untouched field means "use the price
  // derived from the document" and the backend computes it.
  const costPayload = () => (typedCost != null ? { unit_cost: typedCost } : {});

  // "Päivitä osan ostohinta": only for a row landing on an EXISTING part whose
  // registry price differs from what is being booked. Never for a new part.
  const targetInfo = preview?.target_part || null;
  const priceDiffers =
    targetInfo && preview.unit_cost != null && targetInfo.price != null
      ? Math.abs(parseFloat(preview.unit_cost) - parseFloat(targetInfo.price)) >= 0.005
      : null;
  const showUpdatePrice =
    !credit &&
    mode !== 'skip' &&
    !!targetInfo &&
    !preview?.creates_part &&
    (canSeeCosts ? priceDiffers === true : true);

  // The price editor: label names the unit (per kpl, or per 20 l package), the
  // helper derives from the printed Summa, the anchor the backend uses too.
  const docUnit = partUnit;
  const packageMode = effectiveBooking === 'content' && preview?.mode === 'content' && !!preview?.package_size;
  const openPriceEditor = () => {
    if (!costTouched && preview?.parsed_unit_cost != null) setUnitCost(formatPrice(preview.parsed_unit_cost));
    setPriceOpen(true);
  };
  const deviationWarns = !!preview?.price_deviation_warns && typedCost != null;

  const handleConfirm = () => {
    const updatePayload = { update_part_price: updatePrice && showUpdatePrice };
    if (mode === 'skip') {
      onResolve('skip-line', { line_id: line.id });
    } else if (mode === 'stock') {
      onResolve('book-line-stock', {
        line_id: line.id,
        part_id: (showTarget && pickedPart ? pickedPart.id : line.suggested_part) || null,
        ...updatePayload,
        ...costPayload(),
        ...(nonDefault ? { booked_quantity: String(bookedQtyRaw).replace(',', '.'), booked_unit: bookedUnit } : {}),
      });
    } else if (mode === 'received') {
      onResolve('mark-line-received', {
        line_id: line.id,
        maintenance_part_id: line.suggested_maintenance_part,
        update_inventory: bookStock && tracking,
        ...updatePayload,
        ...costPayload(),
      });
    } else {
      onResolve('attach-line', {
        line_id: line.id,
        maintenance_id: woId,
        update_inventory: bookStock && tracking,
        ...updatePayload,
        ...costPayload(),
      });
    }
  };

  const linkSx = { alignSelf: 'flex-start', fontSize: '12.5px', fontWeight: 500 };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
      {/* Mode buttons */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${modes.length}, 1fr)` },
          gap: 1,
          mb: 2,
        }}
      >
        {modes.map((m) => (
          <Box
            key={m.id}
            onClick={() => setMode(m.id)}
            data-testid={`line-mode-${m.id}`}
            sx={{
              p: 1.25,
              borderRadius: '9px',
              cursor: 'pointer',
              border: 1,
              borderColor: mode === m.id ? 'secondary.main' : 'divider',
              bgcolor: mode === m.id ? '#ebf4ff' : 'background.paper',
              transition: 'all 150ms',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, color: mode === m.id ? 'secondary.main' : 'text.secondary' }}>
              {m.icon}
              <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: mode === m.id ? '#2c5282' : 'text.primary' }}>
                {m.label}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '11px', color: 'text.secondary', lineHeight: 1.4 }}>
              {m.sub}
            </Typography>
          </Box>
        ))}
      </Box>

      {mode !== 'skip' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* WO picker */}
          {mode !== 'stock' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '12.5px', fontWeight: 600, minWidth: 92 }}>
                {t('inboundDeliveries.workOrder')}
              </Typography>
              {mode === 'received' ? (
                <Typography sx={{ fontSize: '13px', ...numFont }}>
                  #{line.suggested_maintenance_info?.work_number}{' '}
                  {line.suggested_maintenance_info?.registration_number}
                </Typography>
              ) : (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ flex: 1, minWidth: { xs: 0, sm: 240 } }}
                    options={woOptions}
                    value={pickedWo}
                    inputValue={woInput}
                    loading={woLoading}
                    filterOptions={(options) => options}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={workOrderLabel}
                    renderOption={(props, wo) => (
                      <li {...props} key={wo.id} data-testid={`line-wo-option-${wo.id}`}>
                        {workOrderLabel(wo)}
                      </li>
                    )}
                    onInputChange={(_, value, reason) => {
                      setWoInput(value);
                      if (reason === 'input') searchWorkOrders(value);
                    }}
                    onChange={(_, value) => setPickedWo(value)}
                    noOptionsText={
                      woLoading ? t('inboundDeliveries.woSearching') : t('inboundDeliveries.woSearchNoResults')
                    }
                    data-testid="line-wo-select"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={t('inboundDeliveries.woSearchPlaceholder')}
                        inputProps={{ ...params.inputProps, 'data-testid': 'line-wo-search' }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {woLoading ? <CircularProgress size={14} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
                    {t('inboundDeliveries.woSearchHelp')}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Linked-row info (received mode) */}
          {mode === 'received' && suggestedMp && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.25,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px',
                fontSize: '12.5px',
                flexWrap: 'wrap',
              }}
            >
              <LinkIcon sx={{ fontSize: 16, color: '#2f8c54' }} />
              <Typography variant="body2" color="text.secondary">
                {t('inboundDeliveries.linkingToRow')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {suggestedMp.name}
              </Typography>
              {suggestedMp.code && (
                <Typography variant="body2" sx={{ ...codeFont, fontSize: '12px' }}>
                  {suggestedMp.code}
                </Typography>
              )}
              <Typography variant="body2" color="text.disabled">
                · {t('inboundDeliveries.noNewRow')}
              </Typography>
            </Box>
          )}

          {/* Terminal WO acknowledgement */}
          {needsAck && (
            <Alert severity="warning" icon={<WarningAmberIcon />}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('inboundDeliveries.terminalWoWarning')}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} size="small" data-testid="terminal-ack" />}
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{t('inboundDeliveries.terminalWoAck')}</Typography>}
              />
            </Alert>
          )}

          {/* Credit row returned from stock: preview the balance, and warn when the
              return will enable tracking (or create the part) and land negative */}
          {mode === 'stock' && credit && (
            previewTracked ? (
              <Alert severity="info" icon={<UndoIcon />} data-testid="return-stock-level">
                {t('inboundDeliveries.returnStockLevel', balanceParams)}
              </Alert>
            ) : (
              <Alert severity="warning" icon={<WarningAmberIcon />} data-testid="return-stock-negative">
                <strong>
                  {t(previewPart ? 'inboundDeliveries.returnUntrackedTitle' : 'inboundDeliveries.returnNewPartTitle')}
                </strong>{' '}
                {t(
                  previewPart ? 'inboundDeliveries.returnUntrackedBody' : 'inboundDeliveries.returnNewPartBody',
                  balanceParams
                )}
              </Alert>
            )
          )}

          {/* Package → content toggle: only when a size is known and the matched
              part is not already in that unit */}
          {showToggle && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={bookingMode === 'custom' ? null : bookingMode}
              onChange={(_, value) => value && setBookingMode(value)}
              sx={{ alignSelf: 'flex-start', bgcolor: 'background.paper' }}
            >
              <ToggleButton value="package" data-testid="line-book-package" sx={{ textTransform: 'none' }}>
                {t('inboundDeliveries.bookAsPackage', { qty: formatQuantity(printedQty) })}
              </ToggleButton>
              <ToggleButton value="content" data-testid="line-book-content" sx={{ textTransform: 'none' }}>
                {contentModeLabel(t, formatQuantity(contentQty), contentUnit)}
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {/* What gets booked, from the backend */}
          <BookingSummary t={t} preview={preview} credit={credit} loading={previewLoading} warn={deviationWarns} />

          {/* Target part: where the stock lands when it is not the matched part.
              The litre twin, a picked part, or the part Vahvista creates (a barrel
              row booked as litres, or an unmatched row in any unit). */}
          {showTarget && (
            <Box data-testid="line-content-part" sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', alignItems: 'baseline' }}>
                <Typography variant="body2" color="text.secondary">
                  {targetPart
                    ? t('inboundDeliveries.contentPartExisting', { name: targetPart.name, unit: bookedUnit })
                    : t('inboundDeliveries.contentPartNew', {
                        name: bookingMode === 'content' && content ? content.content_name : line.name,
                        unit: bookedUnit,
                      })}
                </Typography>
                {!twinForUnit && !pickerOpen && (
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => {
                      setPartOptions(defaultPartOptions());
                      setPickerOpen(true);
                    }}
                    data-testid="line-content-part-pick"
                    sx={linkSx}
                  >
                    {t('inboundDeliveries.contentPartPickLink')}
                  </Link>
                )}
              </Box>
              {!twinForUnit && pickerOpen && (
                <Autocomplete
                  size="small"
                  sx={{ maxWidth: 360, bgcolor: 'background.paper' }}
                  options={partOptions}
                  value={pickedPart}
                  loading={partsLoading}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => option.name || ''}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id} data-testid={`line-content-part-option-${option.id}`}>
                      {option.name}
                      {option.code ? ` [${option.code}]` : ''}
                    </li>
                  )}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') searchParts(value);
                  }}
                  onChange={(_, value) => setPickedPart(value)}
                  noOptionsText={
                    partsLoading
                      ? t('inboundDeliveries.contentPartSearching')
                      : t('inboundDeliveries.contentPartNoResults', { unit: bookedUnit })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      autoFocus
                      placeholder={t('inboundDeliveries.contentPartPick', { unit: bookedUnit })}
                      inputProps={{ ...params.inputProps, 'data-testid': 'line-content-part-picker' }}
                    />
                  )}
                />
              )}
            </Box>
          )}

          {/* Edit links */}
          <Box sx={{ display: 'flex', gap: 2.25, flexWrap: 'wrap' }}>
            {mode === 'stock' && !qtyOpen && (
              <Link component="button" type="button" underline="hover" onClick={() => setQtyOpen(true)} data-testid="line-edit-quantity" sx={linkSx}>
                {t('inboundDeliveries.editQuantity')}
              </Link>
            )}
            {canSeeCosts && !priceOpen && (
              <Link component="button" type="button" underline="hover" onClick={openPriceEditor} data-testid="line-edit-price" sx={linkSx}>
                {t('inboundDeliveries.editPrice')}
              </Link>
            )}
          </Box>

          {/* Quantity editor: content per package (derives the total) and the raw override */}
          <Collapse in={mode === 'stock' && qtyOpen} unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '12.5px', fontWeight: 600, minWidth: 150 }}>
                    {t('inboundDeliveries.packageSizeLabel')}
                  </Typography>
                  <DecimalField
                    value={content ? String(content.quantity) : manualSize}
                    disabled={!!content}
                    onChange={(value) => {
                      setManualSize(value);
                      const size = parseFloat(String(value).replace(',', '.'));
                      if (size > 0) {
                        setBookingMode('content');
                        setCustomQty(String(Math.round(printedQty * size * 100) / 100));
                        setCustomUnit(manualUnit);
                      } else if (bookingMode === 'content') {
                        setBookingMode('package');
                      }
                    }}
                    size="small"
                    sx={{ width: 110 }}
                    inputProps={{ 'data-testid': 'line-package-size' }}
                  />
                  <TextField
                    select
                    size="small"
                    label={t('part_unit')}
                    value={content ? content.unit : manualUnit}
                    disabled={!!content}
                    onChange={(e) => {
                      setManualUnit(e.target.value);
                      setCustomUnit(e.target.value);
                    }}
                    sx={{ minWidth: 130 }}
                    inputProps={{ 'data-testid': 'line-package-size-unit' }}
                  >
                    {UNIT_OPTIONS.filter((u) => u !== 'kpl').map((u) => (
                      <MenuItem key={u} value={u}>
                        {u} — {t('unit_' + u)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="caption" color="text.secondary" sx={{ flexBasis: '100%' }}>
                    {t('inboundDeliveries.packageSizeHelp', { qty: formatQuantity(printedQty) })}
                  </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 600, minWidth: 150 }}>
                  {t('inboundDeliveries.bookedQuantityLabel')}
                </Typography>
                <DecimalField
                  value={bookingMode === 'custom' ? customQty : String(bookedQty)}
                  onChange={(value) => {
                    setCustomQty(value);
                    setBookingMode('custom');
                  }}
                  size="small"
                  sx={{ width: 110 }}
                  inputProps={{ 'data-testid': 'line-booked-quantity' }}
                />
                <TextField
                  select
                  size="small"
                  label={t('part_unit')}
                  value={bookingMode === 'custom' ? customUnit : bookedUnit}
                  onChange={(e) => {
                    setCustomUnit(e.target.value);
                    setCustomQty(String(bookedQty));
                    setBookingMode('custom');
                  }}
                  sx={{ minWidth: 130 }}
                  inputProps={{ 'data-testid': 'line-booked-unit' }}
                >
                  {UNIT_OPTIONS.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u} — {t('unit_' + u)}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            </Box>
          </Collapse>

          {/* Price editor */}
          <Collapse in={canSeeCosts && priceOpen} unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 700 }}>
                  {credit
                    ? t('inboundDeliveries.creditPricePerUnitLabel', { unit: docUnit })
                    : packageMode
                    ? t('inboundDeliveries.purchasePricePerPackageLabel', {
                        size: formatQuantity(parseFloat(preview.package_size)),
                        unit: preview.package_unit || bookedUnit,
                      })
                    : t('inboundDeliveries.purchasePricePerUnitLabel', { unit: docUnit })}
                </Typography>
                <DecimalField
                  value={unitCost}
                  onChange={(value) => {
                    setUnitCost(value);
                    setCostTouched(true);
                  }}
                  size="small"
                  autoFocus
                  InputProps={{ endAdornment: <InputAdornment position="end">€</InputAdornment> }}
                  inputProps={{ 'data-testid': 'line-unit-cost' }}
                  sx={{ width: 150, bgcolor: 'background.paper' }}
                />
              </Box>
              {preview?.printed_row_total != null && preview?.gross_unit_price != null && (
                <Typography variant="caption" color="text.secondary" data-testid="line-price-help">
                  {t(
                    preview.doc_vat_basis === 'incl'
                      ? 'inboundDeliveries.priceHelperIncl'
                      : 'inboundDeliveries.priceHelperExcl',
                    {
                      total: formatPrice(preview.printed_row_total),
                      qty: formatQuantity(parseFloat(preview.package_count)),
                      unit: docUnit,
                      perUnit: formatPrice(preview.gross_unit_price),
                      net: formatPrice(preview.parsed_unit_cost),
                    }
                  )}
                </Typography>
              )}
              {deviationWarns && (
                <Alert severity="warning" icon={<WarningAmberIcon />} data-testid="line-price-deviation">
                  {t('inboundDeliveries.priceDeviationWarning', {
                    percent: formatSignedPercent(preview.price_deviation_percent),
                    parsed: formatPrice(preview.parsed_unit_cost),
                  })}
                  {packageMode && (
                    <>
                      {' '}
                      {t(
                        (preview.package_unit || bookedUnit) === 'l'
                          ? 'inboundDeliveries.priceDeviationPackageHint'
                          : 'inboundDeliveries.priceDeviationPackageHintGeneric',
                        {
                          size: formatQuantity(parseFloat(preview.package_size)),
                          unit: preview.package_unit || bookedUnit,
                        }
                      )}
                    </>
                  )}
                </Alert>
              )}
            </Box>
          </Collapse>

          {/* Options */}
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {mode !== 'stock' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={bookStock && tracking && !terminal}
                    onChange={(e) => setBookStock(e.target.checked)}
                    disabled={!tracking || terminal}
                    size="small"
                    data-testid="line-book-stock"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(credit ? 'inboundDeliveries.returnStockOption' : 'inboundDeliveries.bookStockOption')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {!tracking
                        ? t('inboundDeliveries.bookStockDisabled')
                        : inventoryBooked
                        ? t('inboundDeliveries.inventoryAlreadyBookedHint')
                        : credit && !partTracked
                        ? t('inboundDeliveries.returnStockNoTracking', balanceParams)
                        : terminal
                        ? t('inboundDeliveries.bookStockTerminal')
                        : credit
                        ? t('inboundDeliveries.returnStockHelp')
                        : t('inboundDeliveries.bookStockHelp')}
                    </Typography>
                  </Box>
                }
              />
            )}
            {showUpdatePrice && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={updatePrice}
                    onChange={(e) => setUpdatePrice(e.target.checked)}
                    size="small"
                    data-testid="line-update-price"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('inboundDeliveries.updatePriceOption')}
                    </Typography>
                    {canSeeCosts && (
                      <Typography variant="caption" color="text.secondary" sx={{ ...numFont }}>
                        {t('inboundDeliveries.updatePriceDiff', {
                          old: formatPrice(targetInfo.price),
                          new: formatPrice(preview.unit_cost),
                          unit: preview.booked_unit,
                        })}
                      </Typography>
                    )}
                  </Box>
                }
              />
            )}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('inboundDeliveries.skipHelp')}
        </Typography>
      )}

      {/* The work order already carries this part (typically from the API order):
          offer the right action instead of a second, double-billed line. */}
      {duplicate && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }} data-testid="duplicate-part-alert">
          <strong>{t('inboundDeliveries.duplicatePartTitle')}</strong>{' '}
          {t('inboundDeliveries.duplicatePartBody', {
            name: duplicate.name || '',
            quantity: formatQuantity(parseFloat(duplicate.quantity) || 0),
          })}
          <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              data-testid="duplicate-mark-received"
              onClick={() =>
                onResolve('mark-line-received', {
                  line_id: line.id,
                  maintenance_part_id: duplicate.maintenance_part_id,
                  update_inventory: bookStock && tracking,
                  update_part_price: updatePrice && showUpdatePrice,
                  ...costPayload(),
                })
              }
            >
              {t('inboundDeliveries.duplicatePartMarkReceived')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={busy || !woId}
              data-testid="duplicate-add-anyway"
              onClick={() =>
                onResolve('attach-line', {
                  line_id: line.id,
                  maintenance_id: woId,
                  update_inventory: bookStock && tracking,
                  update_part_price: updatePrice && showUpdatePrice,
                  allow_duplicate: true,
                  ...costPayload(),
                })
              }
            >
              {t('inboundDeliveries.duplicatePartAddAnyway')}
            </Button>
          </Box>
        </Alert>
      )}
      {errorDetail && !duplicate && (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="line-action-error">
          {errorDetail.error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Button size="small" onClick={onClose} disabled={busy}>
          {t('Cancel')}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={busy ? <CircularProgress size={14} /> : <CheckIcon />}
          disabled={blocked}
          onClick={handleConfirm}
          data-testid="line-confirm"
        >
          {t('inboundDeliveries.confirmAction')}
        </Button>
      </Box>
    </Box>
  );
}

// --- Detail dialog -----------------------------------------------------------------

function InboundDeliveryNoteDetailDialog({ t, open, noteId, onClose, onChanged }) {
  const navigate = useNavigate();
  const theme = useTheme();
  // A phone has no room for a floating dialog with margins: it goes full screen,
  // which also gives the lines and the document preview the full width.
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentOrganization } = useUserStore();
  const orgId = currentOrganization?.organization_id;
  const tracking = currentOrganization?.inventory_tracking_enabled ?? false;
  const partsTracking = currentOrganization?.parts_order_tracking_enabled ?? false;
  // Purchase prices are stripped by the API without this key; the panel then
  // hides the price editor and the row prints quantities only.
  const canSeeCosts = can(currentOrganization, 'costs.view_purchase_prices');

  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  // A refusal with a machine-readable code (duplicate part, stock already
  // booked): rendered inside the open line panel, which knows the way out.
  const [actionErrorDetail, setActionErrorDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openLineId, setOpenLineIdState] = useState(null);
  // The open panel's live preview, so the collapsed row above it shows the same
  // "40 l · ≈ 7,30 €/l" the panel is about to book.
  const [linePreviews, setLinePreviews] = useState({});
  const handlePreviewChange = useCallback((lineId, preview) => {
    setLinePreviews((prev) => {
      if (preview == null) {
        if (!(lineId in prev)) return prev;
        const next = { ...prev };
        delete next[lineId];
        return next;
      }
      return { ...prev, [lineId]: preview };
    });
  }, []);
  const setOpenLineId = (id) => {
    setActionErrorDetail(null);
    setLinePreviews({});
    setOpenLineIdState(id);
  };
  const [attachmentIndex, setAttachmentIndex] = useState(0);
  const changedRef = useRef(false);

  const fetchNote = useCallback(async () => {
    if (!noteId) return;
    try {
      const response = await axios.get(`/api/inbound-delivery-notes/${noteId}/`);
      setNote(response.data);
    } catch (err) {
      setActionError(err.response?.data?.detail || t('inboundDeliveries.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [noteId, t]);

  useEffect(() => {
    if (open && noteId) {
      setLoading(true);
      setActionError(null);
      setOpenLineId(null);
      setAttachmentIndex(0);
      changedRef.current = false;
      fetchNote();
    }
    if (!open) {
      setNote(null);
    }
  }, [open, noteId, fetchNote]);

  // Poll while the note is still being read so lines appear live.
  const processing = note && (note.processing_status === 'pending' || note.processing_status === 'processing');
  useEffect(() => {
    if (!open || !processing) return;
    const interval = setInterval(fetchNote, 5000);
    return () => clearInterval(interval);
  }, [open, processing, fetchNote]);

  const markChanged = () => {
    changedRef.current = true;
  };

  const handleClose = () => {
    if (changedRef.current) onChanged?.();
    onClose();
  };

  const postAction = async (action, payload = {}, { closeLine = true } = {}) => {
    setBusy(true);
    setActionError(null);
    setActionErrorDetail(null);
    try {
      const response = await axios.post(
        `/api/inbound-delivery-notes/${noteId}/${action}/`,
        payload,
        { headers: { 'X-CSRFToken': getCsrfToken() } }
      );
      setNote(response.data);
      if (closeLine) setOpenLineId(null);
      markChanged();
      return response.data;
    } catch (err) {
      const data = err.response?.data;
      if (data?.code) {
        setActionErrorDetail(data);
      } else {
        setActionError(data?.error || data?.detail || t('inboundDeliveries.actionFailed'));
      }
      return null;
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    const result = await postAction('dismiss');
    if (result) handleClose();
  };

  const handleMarkArrived = async (maintenanceId) => {
    setBusy(true);
    try {
      await axios.post(
        `/api/maintenances/${maintenanceId}/set-parts-order-status/`,
        { status: 'arrived' },
        { headers: { 'X-CSRFToken': getCsrfToken() } }
      );
      markChanged();
      await fetchNote();
    } catch (err) {
      setActionError(err.response?.data?.error || t('inboundDeliveries.actionFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const lines = note?.lines || [];
  const pendingLines = lines.filter((l) => l.resolution === 'pending');
  const attachments = note?.attachments || [];
  const attachment = attachments[attachmentIndex] || null;
  const failed = note?.processing_status === 'failed';
  const workOrderOptions = note?.work_order_options || [];
  // Every part row is a credit → the whole document is a hyvityslähete: nothing
  // arrived, so the order-receipt banners give way to the return guidance.
  const partLines = lines.filter((l) => l.row_type === 'part');
  const creditNote = partLines.length > 0 && partLines.every((l) => l.is_credit);
  // Credit lines on a work order are refunds, not arrivals — they never make a
  // parts order "arrived".
  const resolvedWoIds = [
    ...new Set(lines.filter((l) => !l.is_credit).map((l) => l.resolved_maintenance_id).filter(Boolean)),
  ];
  const arrivedReady = partsTracking && lines.length > 0 && pendingLines.length === 0 && resolvedWoIds.length > 0;

  const fileUrl = (att) =>
    `/api/inbound-delivery-notes/${noteId}/attachments/${att.id}/file/?organization=${orgId}`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '16px',
          m: fullScreen ? 0 : 3,
          height: fullScreen ? '100%' : 'calc(100% - 48px)',
          maxHeight: '100%',
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 2.5 }, py: 1.75, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px' }}>
              {note?.supplier_name || t('inboundDeliveries.unknownSupplier')}
            </Typography>
            {note?.lahete_number && (
              <Typography sx={{ ...numFont, fontSize: '15px' }}>
                {note.lahete_number}
              </Typography>
            )}
            {note && <NoteStatusChip note={note} />}
            {note && creditNote && (
              <Chip
                label={t('inboundDeliveries.creditNoteChip')}
                size="small"
                icon={<UndoIcon sx={{ fontSize: 14 }} />}
                data-testid="credit-note-chip"
                sx={{ height: 22, fontSize: '12px', fontWeight: 600, bgcolor: '#f5f0ff', color: '#553c9a', '& .MuiChip-icon': { color: '#553c9a' } }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {note?.source === 'upload'
              ? t('inboundDeliveries.uploadedBy', { name: note?.created_by_name || '' })
              : note?.sender_email}
            {' · '}
            {note && formatDate(note.email_received_at || note.created_at)}
            {note?.handled_at && (
              <span data-testid="inbound-note-handled">
                {' · '}
                {t('inboundDeliveries.handledBy', {
                  date: formatDate(note.handled_at),
                  name:
                    note.handled_by_kind === 'auto'
                      ? t('inboundDeliveries.handledAutomatically')
                      : note.handled_by_name || t('inboundDeliveries.handledByUnknown'),
                })}
              </span>
            )}
          </Typography>
        </Box>
        {/* Re-reads the document totals + suggestions. Closes the open line panel,
            so a prefilled purchase price is re-derived from the fresh values. */}
        {note && !processing && !failed && pendingLines.length > 0 && (
          <Tooltip title={t('inboundDeliveries.rematchTooltip')}>
            <span>
              <IconButton
                onClick={() => postAction('rematch')}
                size="small"
                disabled={busy}
                data-testid="inbound-rematch"
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && note && (
          <Box
            data-testid="inbound-detail-body"
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.35fr 1fr' },
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              overflowY: { xs: 'auto', md: 'hidden' },
              overflowX: 'hidden',
            }}
          >
            {/* Left: parsed content */}
            <Box sx={{ overflowY: { md: 'auto' }, minWidth: 0, p: { xs: 1.5, sm: 2.5 }, borderRight: { md: 1 }, borderColor: { md: 'divider' } }}>
              {actionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                  {actionError}
                </Alert>
              )}

              {processing ? (
                <Alert severity="info" icon={<HourglassTopIcon />} sx={{ mb: 2 }}>
                  <strong>{t('inboundDeliveries.processingTitle')}</strong>{' '}
                  {t('inboundDeliveries.processingBody')}
                </Alert>
              ) : failed && lines.length === 0 ? (
                <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2 }}>
                  <strong>{t('inboundDeliveries.failedTitle')}</strong>{' '}
                  {note.processing_error || ''} {t('inboundDeliveries.failedBody')}
                </Alert>
              ) : null}

              {/* Credit note: return guidance instead of order receipt */}
              {!processing && !failed && creditNote && !note.confirmed_order && (
                <Alert
                  severity="info"
                  icon={<UndoIcon />}
                  data-testid="credit-note-banner"
                  sx={{ mb: 2, bgcolor: '#f5f0ff', color: '#553c9a', '& .MuiAlert-icon': { color: '#553c9a' } }}
                >
                  <strong>{t('inboundDeliveries.creditNoteTitle')}</strong>{' '}
                  {t('inboundDeliveries.creditNoteBody')}
                </Alert>
              )}

              {/* The suggested order was received by hand or cancelled meanwhile:
                  say so instead of offering a Vastaanota that can only fail. */}
              {!processing && !creditNote && note.suggested_order && !note.confirmed_order && note.suggested_order.receivable === false && (
                <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }} data-testid="order-not-receivable-banner">
                  <strong>
                    {t('inboundDeliveries.orderNotReceivableTitle', {
                      reference: note.suggested_order.display_reference,
                      status: t('purchaseOrders.status.' + note.suggested_order.status),
                    })}
                  </strong>{' '}
                  {t('inboundDeliveries.orderNotReceivableBody')}
                </Alert>
              )}

              {/* Order match banner */}
              {!processing && !creditNote && note.suggested_order && !note.confirmed_order && note.suggested_order.receivable !== false && (
                <Alert
                  severity="info"
                  icon={<LightbulbOutlinedIcon />}
                  sx={{ mb: 2, bgcolor: '#ebf4ff', '& .MuiAlert-icon': { color: '#3182ce' } }}
                  action={
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={busy ? <CircularProgress size={14} /> : <InventoryIcon />}
                      disabled={busy}
                      onClick={() =>
                        postAction('confirm-order', {
                          order_id: note.suggested_order.id,
                          update_inventory: true,
                        })
                      }
                      data-testid="confirm-order-button"
                    >
                      {t('inboundDeliveries.receiveOrder')}
                    </Button>
                  }
                >
                  <strong data-testid="order-banner-title">
                    {t(
                      note.order_match_kind === 'exact'
                        ? 'inboundDeliveries.orderExactTitle'
                        : 'inboundDeliveries.orderSuggestionTitle',
                      { reference: note.suggested_order.display_reference }
                    )}
                  </strong>
                  <Box component="ul" sx={{ m: '4px 0 0', pl: 2.25 }}>
                    {(note.match_reasons || []).map((reason, index) => {
                      const text = translateReason(t, reason);
                      return text ? <li key={index}>{text}</li> : null;
                    })}
                  </Box>
                </Alert>
              )}
              {note.confirmed_order && (
                <Alert severity="success" icon={<TaskAltIcon />} sx={{ mb: 2 }}>
                  <strong>
                    {t('inboundDeliveries.orderConfirmedTitle', {
                      reference: note.confirmed_order.display_reference,
                    })}
                  </strong>{' '}
                  {t('inboundDeliveries.orderConfirmedBody')}
                </Alert>
              )}
              {!processing && !failed && !creditNote && !note.suggested_order && !note.confirmed_order && lines.length > 0 && (
                <Alert severity="info" icon={<SearchOffIcon />} sx={{ mb: 2, bgcolor: 'background.default', color: 'text.secondary' }}>
                  <strong>{t('inboundDeliveries.noOrderMatchTitle')}</strong>{' '}
                  {t('inboundDeliveries.noOrderMatchBody')}
                </Alert>
              )}

              {/* Document validation flags. The stored sum_mismatch gives way to the
                  live reconciliation line under "Lähetteen tiedot", which has the figures. */}
              {(note.validation_flags || []).filter((flag) => !(flag === 'sum_mismatch' && note.net_reconciliation)).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                  {note.validation_flags.filter((flag) => !(flag === 'sum_mismatch' && note.net_reconciliation)).map((flag) => {
                    const info = INFO_FLAGS.has(flag);
                    return (
                      <Box
                        key={flag}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          p: '9px 12px',
                          bgcolor: info ? '#ebf4ff' : '#fef3e2',
                          border: `1px solid ${info ? '#bcd7fb' : '#fbd9a5'}`,
                          borderRadius: '8px',
                          fontSize: '12.5px',
                          color: info ? '#2c5282' : '#92400e',
                        }}
                      >
                        {info ? (
                          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#2c5282', flexShrink: 0 }} />
                        ) : (
                          <WarningAmberIcon sx={{ fontSize: 16, color: '#d97706', flexShrink: 0 }} />
                        )}
                        {t(FLAG_LABEL_KEYS[flag] || flag)}
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Header info */}
              {!processing && (lines.length > 0 || note.supplier_name) && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                    {t('inboundDeliveries.noteInfo')}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', mt: 0.75, minWidth: 0 }}>
                    <InfoItem label={t('inboundDeliveries.columns.supplier')} value={note.supplier_name || '—'} />
                    <InfoItem label={t('inboundDeliveries.columns.number')} value={note.lahete_number || '—'} mono />
                    <InfoItem label={t('inboundDeliveries.documentDate')} value={note.document_date ? formatDate(note.document_date) : '—'} />
                    <InfoItem label={t('inboundDeliveries.reference')} value={note.reference_text || '—'} mono />
                    <InfoItem
                      label={t('inboundDeliveries.columns.registrations')}
                      value={(note.registration_numbers || []).join('  ') || '—'}
                      mono
                    />
                    <InfoItem
                      label={t('inboundDeliveries.columns.totalNet')}
                      value={note.total_net != null ? `${formatPrice(note.total_net)} €` : '—'}
                    />
                    <InfoItem
                      label={t('inboundDeliveries.totalGross')}
                      value={
                        note.totals?.gross != null ? `${formatPrice(note.totals.gross)} €` : '—'
                      }
                    />
                  </Box>
                  {note.net_reconciliation && <ReconciliationLine t={t} reconciliation={note.net_reconciliation} />}
                </Box>
              )}

              {/* Lines */}
              {lines.length > 0 && (
                <Box>
                  <Typography variant="overline" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.08em' }}>
                    {t('inboundDeliveries.linesTitle', { count: lines.length })}
                    {pendingLines.length > 0 &&
                      ` · ${t('inboundDeliveries.linesPending', { count: pendingLines.length })}`}
                  </Typography>
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '10px', overflow: 'hidden', mt: 0.75 }}>
                    {lines.map((line) => {
                      const isOpen = openLineId === line.id;
                      const done = line.resolution !== 'pending';
                      // The open panel's live preview wins over the server default.
                      const rowPreview = (isOpen && linePreviews[line.id]) || line.booking_preview || null;
                      const reasons = (line.suggestion_reasons || [])
                        .filter((r) => !['wo_part_price_diff', 'wo_terminal_state'].includes(r.code))
                        .map((r) => translateReason(t, r))
                        .filter(Boolean);
                      return (
                        <React.Fragment key={line.id}>
                          <Box
                            data-testid={`inbound-line-${line.id}`}
                            sx={{
                              display: 'flex',
                              flexWrap: { xs: 'wrap', sm: 'nowrap' },
                              gap: { xs: 1, sm: 1.5 },
                              rowGap: 0.75,
                              alignItems: 'flex-start',
                              p: { xs: '10px 12px', sm: '12px 14px' },
                              bgcolor: done ? '#fbfdfb' : 'background.paper',
                              borderBottom: isOpen ? 0 : 1,
                              borderColor: 'divider',
                              '&:last-of-type': { borderBottom: 0 },
                            }}
                          >
                            <Typography sx={{ color: 'text.disabled', ...numFont, fontSize: '12px', width: 22, flexShrink: 0, pt: '3px' }}>
                              {line.line_number}
                            </Typography>
                            {/* On xs this claims the whole first line, so the
                                numbers and the button wrap under it. */}
                            <Box sx={{ flex: 1, minWidth: { xs: 'calc(100% - 30px)', sm: 0 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: '13.5px', fontWeight: 600, overflowWrap: 'anywhere' }}>
                                  {line.name}
                                </Typography>
                                {line.row_type !== 'part' && (
                                  <Chip
                                    label={t(ROW_TYPE_LABEL_KEYS[line.row_type])}
                                    size="small"
                                    sx={{ height: 20, fontSize: '11px', bgcolor: '#fef3e2', color: '#b45309', fontWeight: 600 }}
                                  />
                                )}
                                {line.is_credit && (
                                  <Chip
                                    label={t('inboundDeliveries.creditChip')}
                                    size="small"
                                    data-testid={`line-credit-${line.id}`}
                                    sx={{ height: 20, fontSize: '11px', bgcolor: '#f5f0ff', color: '#553c9a', fontWeight: 600 }}
                                  />
                                )}
                                <ConfidenceChip value={line.confidence} t={t} />
                                {(line.validation_flags || []).map((flag) => (
                                  <Tooltip key={flag} title={t(FLAG_LABEL_KEYS[flag] || flag)}>
                                    <Chip
                                      icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                                      label={t(FLAG_LABEL_KEYS[flag] || flag)}
                                      size="small"
                                      sx={{ height: 20, fontSize: '11px', bgcolor: '#fee2e2', color: '#c53030', fontWeight: 600, maxWidth: 260 }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: 'text.secondary' }}>
                                {line.product_code && (
                                  <Typography sx={{ ...codeFont, fontSize: '12px' }}>
                                    {line.product_code}
                                  </Typography>
                                )}
                                {line.registration_number && (
                                  <Typography sx={{ ...plateFont, fontSize: '12px', opacity: 0.8 }}>
                                    · {line.registration_number}
                                  </Typography>
                                )}
                              </Box>
                              {!done && reasons.length > 0 && (
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 0.5 }}>
                                  <LightbulbOutlinedIcon sx={{ fontSize: 14, color: '#2c5282', mt: '2px', flexShrink: 0 }} />
                                  <Typography sx={{ fontSize: '12px', color: '#2c5282', lineHeight: 1.45 }}>
                                    {reasons.join('. ')}
                                  </Typography>
                                </Box>
                              )}
                              {/* The automatic run left this row for a person: say why. */}
                              {!done && line.auto_hold_reason && (
                                <Box sx={{ mt: 0.5 }}>
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    data-testid={`line-auto-hold-${line.id}`}
                                    label={`${t('inboundDeliveries.autoHoldTitle')}: ${t(
                                      `inboundDeliveries.autoHold.${line.auto_hold_reason}`,
                                      { defaultValue: line.auto_hold_reason }
                                    )}`}
                                    sx={{ height: 20, fontSize: '11px' }}
                                  />
                                </Box>
                              )}
                            </Box>
                            <Box
                              sx={{
                                textAlign: { xs: 'left', sm: 'right' },
                                ml: { xs: '30px', sm: 0 },
                                flexShrink: 0,
                                ...numFont,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, gap: 0.75 }}>
                                {line.discount_percent != null && parseFloat(line.discount_percent) !== 0 && (
                                  <Chip
                                    label={`−${formatPercent(line.discount_percent)} %`}
                                    size="small"
                                    sx={{ height: 18, fontSize: '10.5px', fontWeight: 700, bgcolor: '#e6ffed', color: '#2f8c54' }}
                                  />
                                )}
                                <Typography sx={{ fontSize: '13px', ...numFont }}>
                                  {formatQuantity(line.quantity)} × {line.unit_price != null ? `${formatPrice(line.unit_price)} €` : '—'}
                                </Typography>
                              </Box>
                              {line.line_total != null && (
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, ...numFont }}>
                                  {formatPrice(line.line_total)} €
                                  {line.price_includes_vat && <VatLabel>{t('inboundDeliveries.vatIncludedShort')}</VatLabel>}
                                </Typography>
                              )}
                              {/* Third line: the booking result when the row is (or will be)
                                  booked as its content ("40 l · ≈ 7,30 €/l"), else the
                                  per-unit ostohinta. A booked row's preview comes from the
                                  ledger, so a typed price shows here too. */}
                              {rowPreview && rowPreview.mode === 'content' ? (
                                <Typography
                                  data-testid={`line-booking-${line.id}`}
                                  sx={{ fontSize: '11.5px', ...numFont, color: 'text.secondary', mt: '1px' }}
                                >
                                  {formatQuantity(parseFloat(rowPreview.booked_qty))} {rowPreview.booked_unit}
                                  {rowPreview.unit_cost != null &&
                                    ` · ≈ ${formatPrice(rowPreview.unit_cost)} €/${rowPreview.booked_unit}`}
                                  {rowPreview.unit_cost != null && line.price_includes_vat && (
                                    <VatLabel>{t('inboundDeliveries.vatExcludedShort')}</VatLabel>
                                  )}
                                </Typography>
                              ) : (
                                (done && rowPreview?.unit_cost != null ? rowPreview.unit_cost : line.net_unit_cost) != null && (
                                  <Tooltip title={t('inboundDeliveries.purchasePriceTooltip')}>
                                    <Typography
                                      data-testid={`line-net-cost-${line.id}`}
                                      sx={{ fontSize: '11.5px', ...numFont, color: 'text.secondary', mt: '1px' }}
                                    >
                                      {t('inboundDeliveries.purchasePriceShort', {
                                        price: formatPrice(done && rowPreview?.unit_cost != null ? rowPreview.unit_cost : line.net_unit_cost),
                                      })}
                                      {line.price_includes_vat && <VatLabel>{t('inboundDeliveries.vatExcludedShort')}</VatLabel>}
                                    </Typography>
                                  </Tooltip>
                                )
                              )}
                            </Box>
                            <Box sx={{ flexShrink: 0, minWidth: { xs: 0, sm: 110 }, flex: { xs: 1, sm: 'none' }, textAlign: 'right' }}>
                              {done ? (
                                <Box>
                                  <ResolutionChip resolution={line.resolution} credit={!!line.is_credit} />
                                  {/* Where it went + who put it there: the audit trail
                                      for a lähete somebody handled weeks ago. */}
                                  {line.resolved_maintenance_info && (
                                    <Box>
                                      <Button
                                        size="small"
                                        variant="text"
                                        data-testid={`line-resolved-wo-${line.id}`}
                                        onClick={() => {
                                          handleClose();
                                          navigate(`/maintenances/${line.resolved_maintenance_info.id}`);
                                        }}
                                        sx={{ fontSize: '12px', px: 0.5, py: 0, minWidth: 0 }}
                                      >
                                        {t('inboundDeliveries.openWorkOrder', {
                                          number: line.resolved_maintenance_info.work_number,
                                        })}
                                      </Button>
                                    </Box>
                                  )}
                                  {!line.resolved_maintenance_info && line.resolved_part_info && (
                                    <Typography
                                      data-testid={`line-resolved-part-${line.id}`}
                                      sx={{ fontSize: '11.5px', color: 'text.secondary', mt: '2px' }}
                                    >
                                      {line.resolved_part_info.code || line.resolved_part_info.name}
                                    </Typography>
                                  )}
                                  {/* Booked as something other than the printed row:
                                      "20 l" for a barrel, "2 kpl (lähetteellä 1)" for a fix. */}
                                  {line.booked_quantity != null && (
                                    <Typography
                                      data-testid={`line-booked-qty-${line.id}`}
                                      sx={{ fontSize: '11.5px', color: 'text.secondary', mt: '2px' }}
                                    >
                                      {t(
                                        line.booked_unit && line.booked_unit !== 'kpl'
                                          ? 'inboundDeliveries.bookedCaption'
                                          : 'inboundDeliveries.bookedCaptionDiffers',
                                        {
                                          qty: formatQuantity(parseFloat(line.booked_quantity)),
                                          unit: line.booked_unit || 'kpl',
                                          printed: formatQuantity(Math.abs(parseFloat(line.quantity))),
                                        }
                                      )}
                                    </Typography>
                                  )}
                                  {(line.auto_resolved || line.resolved_by_name || line.resolved_at) && (
                                    <Typography
                                      sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}
                                      data-testid={`line-resolved-by-${line.id}`}
                                    >
                                      {[
                                        line.auto_resolved
                                          ? t('inboundDeliveries.handledAutomatically')
                                          : line.resolved_by_name,
                                        line.resolved_at && formatDate(line.resolved_at),
                                      ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Button
                                  size="small"
                                  variant={isOpen ? 'text' : 'outlined'}
                                  endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                  onClick={() => setOpenLineId(isOpen ? null : line.id)}
                                  data-testid={`line-toggle-${line.id}`}
                                >
                                  {isOpen ? t('Close') : t('inboundDeliveries.allocate')}
                                </Button>
                              )}
                            </Box>
                          </Box>
                          {isOpen && (
                            <LineActions
                              t={t}
                              note={note}
                              line={line}
                              workOrderOptions={workOrderOptions}
                              tracking={tracking}
                              busy={busy}
                              errorDetail={actionErrorDetail}
                              onResolve={postAction}
                              onClose={() => setOpenLineId(null)}
                              canSeeCosts={canSeeCosts}
                              onPreviewChange={handlePreviewChange}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, mt: 1 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '1px' }} />
                    <Typography variant="caption" color="text.disabled">
                      {t('inboundDeliveries.linesFootnote')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Arrived one-click */}
              {arrivedReady && (
                <Alert
                  severity="success"
                  icon={<LocalShippingIcon />}
                  sx={{ mt: 2 }}
                  action={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {resolvedWoIds.map((maintenanceId) => (
                        <Button
                          key={maintenanceId}
                          variant="outlined"
                          size="small"
                          disabled={busy}
                          onClick={() => handleMarkArrived(maintenanceId)}
                        >
                          {t('inboundDeliveries.markArrived')}
                        </Button>
                      ))}
                    </Box>
                  }
                >
                  <strong>{t('inboundDeliveries.allAllocatedTitle')}</strong>{' '}
                  {t('inboundDeliveries.allAllocatedBody')}
                </Alert>
              )}
            </Box>

            {/* Right: original document. minWidth 0: a long attachment tab strip
                scrolls inside this column instead of squeezing the left one. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: { xs: 320, md: 0 }, minWidth: 0, bgcolor: 'background.default' }}>
              {/* Attachment tabs */}
              <Box sx={{ display: 'flex', gap: 0.75, p: '10px 14px', borderBottom: 1, borderColor: 'divider', overflowX: 'auto', flexShrink: 0 }}>
                {attachments.map((att, index) => (
                  <Box
                    key={att.id}
                    onClick={() => setAttachmentIndex(index)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 0.6,
                      borderRadius: '7px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      border: 1,
                      borderColor: attachmentIndex === index ? 'secondary.main' : 'divider',
                      bgcolor: attachmentIndex === index ? '#ebf4ff' : 'background.paper',
                      color: attachmentIndex === index ? '#2c5282' : 'text.secondary',
                    }}
                  >
                    {KIND_ICONS[att.detected_kind] || <DescriptionOutlinedIcon sx={{ fontSize: 15 }} />}
                    {att.original_filename}
                    {att.parse_status === 'unsupported' && (
                      <Chip label={t('inboundDeliveries.unsupported')} size="small" sx={{ height: 17, fontSize: '10px' }} />
                    )}
                  </Box>
                ))}
              </Box>
              {/* Preview */}
              <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {attachment ? (
                  <AttachmentPreview
                    t={t}
                    attachment={attachment}
                    url={fileUrl(attachment)}
                    previewUrl={`/api/inbound-delivery-notes/${noteId}/attachments/${attachment.id}/preview/?organization=${orgId}`}
                  />
                ) : (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.disabled' }}>
                    <DescriptionOutlinedIcon sx={{ fontSize: 38 }} />
                    <Typography variant="body2">{t('inboundDeliveries.noAttachments')}</Typography>
                    {note.body_text && (
                      <Box component="pre" sx={{ textAlign: 'left', p: 2, fontSize: '12px', whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                        {note.body_text}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              {/* File meta bar */}
              {attachment && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1.75, py: 1, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.disabled">
                    {formatBytes(attachment.size_bytes)}
                  </Typography>
                  {attachment.charset_detected && (
                    <Typography variant="caption" color="text.disabled">
                      · {attachment.charset_detected}
                    </Typography>
                  )}
                  {attachment.parser_used && (
                    <Typography variant="caption" color="text.disabled">
                      · {attachment.parser_used}
                    </Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => window.open(fileUrl(attachment), '_blank')}
                  >
                    {t('inboundDeliveries.download')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      {note && !loading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.25,
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          {note.status !== 'dismissed' && (
            <Button
              color="error"
              size="small"
              startIcon={<BlockIcon />}
              onClick={handleDismiss}
              disabled={busy}
              data-testid="dismiss-note"
              sx={{ flex: { xs: '0 0 auto', sm: 'none' }, order: { xs: 1, sm: 0 } }}
            >
              {t('inboundDeliveries.dismiss')}
            </Button>
          )}
          <Box sx={{ flex: 1, display: { xs: 'none', sm: 'block' } }} />
          {pendingLines.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                order: { xs: 2, sm: 0 },
                flex: { xs: '1 1 auto', sm: '0 1 auto' },
                textAlign: { xs: 'right', sm: 'left' },
              }}
            >
              {t('inboundDeliveries.pendingFooter', { count: pendingLines.length })}
            </Typography>
          )}
          {note.status === 'new' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityIcon />}
              onClick={() => postAction('review')}
              disabled={busy}
              data-testid="mark-reviewed"
              sx={{ flex: { xs: '1 1 100%', sm: 'none' }, order: { xs: 3, sm: 0 } }}
            >
              {t('inboundDeliveries.markReviewed')}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckIcon />}
            onClick={handleClose}
            data-testid="detail-done"
            sx={{ flex: { xs: '1 1 100%', sm: 'none' }, order: { xs: 4, sm: 0 } }}
          >
            {t('inboundDeliveries.done')}
          </Button>
        </Box>
      )}
    </Dialog>
  );
}

// "sis. alv" / "alv 0 %" next to a row amount, only rendered on a VAT-inclusive document.
function VatLabel({ children }) {
  return (
    <Box component="span" sx={{ fontFamily: 'inherit', fontSize: '10.5px', fontWeight: 400, color: 'text.disabled', ml: 0.5 }}>
      {children}
    </Box>
  );
}

/**
 * One line under "Lähetteen tiedot": do the parsed rows add up to the printed
 * Veroton (or Yhteensä when only that was printed)? Three states: an alv 0 %
 * document that reconciles, a VAT-inclusive document (say so, the rows are
 * booked net), and a mismatch with the difference. Without the cost permission
 * the backend blanks the amounts and only the status prints.
 */
function ReconciliationLine({ t, reconciliation }) {
  const r = reconciliation;
  const amounts = r.rows_total != null && r.document_total != null;
  const gross = r.compared_to === 'gross';
  let severity;
  let key;
  if (!r.matches) {
    severity = 'warning';
    key = !amounts ? 'reconcileMismatchNoAmounts' : gross ? 'reconcileMismatchGross' : 'reconcileMismatch';
  } else if (r.vat_basis === 'incl') {
    severity = 'info';
    key = !amounts ? 'reconcileOkInclNoAmounts' : gross ? 'reconcileOkInclGross' : 'reconcileOkIncl';
  } else {
    severity = 'success';
    key = !amounts ? 'reconcileOkNoAmounts' : gross ? 'reconcileOkGross' : 'reconcileOk';
  }
  return (
    <Alert
      severity={severity}
      icon={severity === 'success' ? <CheckIcon fontSize="inherit" /> : severity === 'info' ? <InfoOutlinedIcon fontSize="inherit" /> : <WarningAmberIcon fontSize="inherit" />}
      data-testid="note-reconciliation"
      data-state={severity}
      sx={{ mt: 1.5, py: 0.5, fontSize: '12.5px', borderRadius: '8px' }}
    >
      {t(`inboundDeliveries.${key}`, {
        rows: amounts ? formatPrice(r.rows_total) : '',
        doc: amounts ? formatPrice(r.document_total) : '',
        diff: amounts ? formatPrice(Math.abs(parseFloat(r.difference))) : '',
        rate: r.vat_rate != null ? formatPercent(r.vat_rate) : '',
      })}
    </Alert>
  );
}

function InfoItem({ label, value, mono = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, overflowWrap: 'anywhere', ...(mono ? { ...numFont } : {}) }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function PreviewUnavailable({ t, attachment, url }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, minHeight: 280, p: 3, textAlign: 'center', color: 'text.secondary' }}>
      <DescriptionOutlinedIcon sx={{ fontSize: 38, color: 'text.disabled' }} />
      <Typography variant="subtitle2">
        {attachment.parse_status === 'failed'
          ? t('inboundDeliveries.previewFailed')
          : t('inboundDeliveries.previewUnavailable')}
      </Typography>
      <Typography variant="caption" sx={{ maxWidth: 280 }}>
        {attachment.parse_error || t('inboundDeliveries.previewStoredNote')}
      </Typography>
      <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => window.open(url, '_blank')} sx={{ mt: 0.5 }}>
        {t('inboundDeliveries.download')} {attachment.original_filename}
      </Button>
    </Box>
  );
}

function AttachmentPreview({ t, attachment, url, previewUrl }) {
  const unavailable = <PreviewUnavailable t={t} attachment={attachment} url={url} />;
  // A spreadsheet is drawn from its cells whatever the parse outcome was.
  if (attachment.detected_kind === 'spreadsheet') {
    return <SpreadsheetPreview t={t} url={previewUrl} fallback={unavailable} />;
  }
  if (
    attachment.parse_status === 'unsupported' ||
    attachment.parse_status === 'failed' ||
    attachment.detected_kind === 'unknown' ||
    attachment.detected_kind === 'zip'
  ) {
    return unavailable;
  }
  if (attachment.detected_kind === 'image') {
    return (
      <Box sx={{ p: 1.5, textAlign: 'center' }}>
        <img src={url} alt={attachment.original_filename} style={{ maxWidth: '100%', borderRadius: 8 }} />
      </Box>
    );
  }
  // PDF and text: the browser renders both natively in an iframe (follows the 302
  // to the signed URL with session cookies on the initial same-origin request).
  return (
    <iframe
      src={url}
      title={attachment.original_filename}
      style={{ width: '100%', height: '100%', minHeight: 380, border: 0, background: '#fff' }}
    />
  );
}

export default withTranslation()(InboundDeliveryNoteDetailDialog);
